import classNames from 'classnames';
import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { RoutePath, routeAccess } from 'src/router';

import { useSelector } from 'src/store';
import {
  isLoggedInSelector,
  isClientAdminSelector,
  authRoleSelector,
  authRoleListSelector,
} from 'src/store/selectors/authSelector';
import {
  tenantBrandingLogoUrlSelector,
  tenantBrandingLogoAltSelector,
  tenantBrandingNameSelector,
} from 'src/store/selectors/tenantBrandingSelector';
import { selectedProjectSelector, isProjectDBCreationSelector } from 'src/store/selectors/projectSelector';
import { selectedMappingProjectSelector } from 'src/store/selectors/mappingProjectSelector';
import { selectedConfigurationSelector } from 'src/store/selectors/configurationSelector';
import {
  userProductPermissionsSelector,
  hasDataAuditAccessSelector,
} from 'src/store/selectors/userProductPermissionsSelector';

import { LinkWithParams, Navigation, NavigationLocalConfig } from '@itm/shared-frontend/lib/components';
import {
  MenuIcon,
  MigrationIcon,
  AuditIcon,
  DataTranslationsIcon,
  HideIcon,
  SettingIcon,
} from '@itm/shared-frontend/lib/components/icons';

import Filters from 'src/components/Filters';

import { clientPortalDashboardUrl, clientPortalDomain, isAzureTargetPlatform } from 'src/utils/constants';

import { Role, GlobalRole } from 'src/types';

import styles from './Sidebar.module.scss';

type Props = Readonly<{
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
}>;

function Sidebar({ isActive, setIsActive }: Props) {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const userRole = useSelector(authRoleSelector);
  const userRoleListFromToken = useSelector(authRoleListSelector);

  const brandingLogoUrl = useSelector(tenantBrandingLogoUrlSelector);
  const brandingLogoAlt = useSelector(tenantBrandingLogoAltSelector);
  const brandingName = useSelector(tenantBrandingNameSelector);

  const project = useSelector(selectedProjectSelector);
  const isProjectDBCreation = useSelector(isProjectDBCreationSelector);

  const mappingProject = useSelector(selectedMappingProjectSelector);
  const configuration = useSelector(selectedConfigurationSelector);

  const userPermissionList = useSelector(userProductPermissionsSelector);
  const isClientAdmin = useSelector(isClientAdminSelector);
  const hasDataAuditAccess = useSelector(hasDataAuditAccessSelector);

  const canAccessOnboarding = !isClientAdmin || hasDataAuditAccess;

  const userRoleList = useMemo<GlobalRole[]>(() => {
    if (!userRole) return [];
    if (userRole === Role.SuperAdmin || userRole === Role.Support) return [userRole] as unknown as GlobalRole[];

    const clientPortalRole = userRoleListFromToken.find((role) => role.startsWith('ClientPortal'));
    const productRolesByCompany = [...new Set(userPermissionList.map(({ productRoleName }) => productRoleName))];

    return clientPortalRole ? [clientPortalRole, ...productRolesByCompany] : productRolesByCompany;
  }, [userPermissionList, userRole, userRoleListFromToken]);

  const navigationLocalConfig = useMemo<NavigationLocalConfig<Role | GlobalRole[]>>(() => {
    const projectId = project?.id || '';
    const mappingProjectId = mappingProject?.id || '';
    const configurationId = configuration?.id || '';

    return [
      {
        name: 'All Products',
        section: 'products',
        path: clientPortalDashboardUrl,
        icon: () => <MenuIcon />,
      },
      {
        name: 'Additional Options',
        section: 'products',
        path: RoutePath.additionalOptionsRoot,
        isInternalRoute: true,
        icon: () => <SettingIcon />,
        role: { allowList: routeAccess.additionalOptionsRoot },
        children: [
          {
            name: 'SQL server credentials',
            path: RoutePath.additionalOptionsSqlCredentialsRoot,
            children: [
              {
                name: 'User SQL credentials',
                path: RoutePath.additionalOptionsSqlCredentialsUsers,
              },
            ],
          },
          {
            name: 'Product information',
            path: RoutePath.additionalOptionsProductInfoRoot,
            role: { allowList: routeAccess.additionalOptionsProductInfoRoot },
            children: [
              {
                name: 'Technical details',
                path: RoutePath.additionalOptionsProductInfoDetails,
              },
            ],
          },
        ],
      },

      {
        name: 'Migrations',
        section: 'products',
        path: RoutePath.migrationRoot,
        isInternalRoute: true,
        icon: () => <MigrationIcon />,
        role: {
          allowList: routeAccess.migrationRoot,
        },
        children: [
          {
            name: 'Projects',
            path: RoutePath.migrationProjectsRoot,
            children: [
              {
                name: 'Create new project',
                path: RoutePath.migrationProjectsCreateRoot,
              },
              {
                name: 'All projects',
                path: RoutePath.migrationProjectsList,
              },
              {
                name: isProjectDBCreation ? null : project?.name,
                path: generatePath(RoutePath.migrationProjectRoot, { projectId: project?.id || '' }),
                children: [
                  {
                    name: 'Project Details',
                    path: generatePath(RoutePath.migrationProjectDetails, { projectId }),
                  },
                  {
                    name: 'Data Dictionary',
                    path: generatePath(RoutePath.migrationProjectDataDictionaryRoot, { projectId }),
                  },
                  {
                    name: 'Project Databases',
                    path: generatePath(RoutePath.migrationProjectDatabasesRoot, { projectId }),
                  },
                  {
                    name: 'Project Templates',
                    path: generatePath(RoutePath.migrationProjectTemplatesRoot, { projectId }),
                  },
                  {
                    name: 'Project User Permissions',
                    path: generatePath(RoutePath.migrationProjectUsersRoot, { projectId }),
                  },
                ],
              },
            ],
          },
          {
            name: 'Master Templates',
            path: RoutePath.masterTemplatesRoot,
            role: { allowList: routeAccess.masterTemplatesRoot },
            children: [
              {
                name: 'Create new template',
                path: RoutePath.masterTemplatesCreate,
              },
              {
                name: 'All templates',
                path: RoutePath.masterTemplatesList,
              },
            ],
          },
          {
            name: 'Master data dictionary',
            path: RoutePath.masterDataDictionaryRoot,
            role: { allowList: routeAccess.masterDataDictionaryRoot },
            children: [
              {
                name: 'Create new dictionary',
                path: RoutePath.masterDataDictionaryCreateRoot,
              },
              {
                name: 'All master dictionaries',
                path: RoutePath.masterDataDictionaryList,
              },
            ],
          },
          {
            name: 'Applications',
            path: RoutePath.applicationRoot,
            children: [
              {
                name: 'Create new application',
                path: RoutePath.applicationCreate,
              },
              {
                name: 'All applications',
                path: RoutePath.applicationList,
              },
            ],
          },
        ],
      },
      {
        name: 'Fusion PSI Configuration',
        section: 'products',
        path: RoutePath.translationsRoot,
        isInternalRoute: true,
        icon: () => <DataTranslationsIcon />,
        role: {
          allowList: routeAccess.translationsRoot,
        },
        children: [
          {
            name: 'Mappings',
            path: RoutePath.translationsMappingsRoot,
            children: [
              {
                name: 'Create new mapping',
                path: RoutePath.translationsMappingsCreateRoot,
              },
              {
                name: 'All Mappings',
                path: RoutePath.translationsMappingsList,
              },
              {
                name: mappingProject?.name,
                path: generatePath(RoutePath.translationsMappingProjectRoot, { mappingProjectId }),
                children: [
                  {
                    name: 'File Structure',
                    path: generatePath(RoutePath.translationsMappingProjectFileStructure, { mappingProjectId }),
                  },
                  {
                    name: 'Mapping Details',
                    path: generatePath(RoutePath.translationsMappingProjectDetails, { mappingProjectId }),
                  },
                  {
                    name: 'Mapping Templates',
                    path: generatePath(RoutePath.translationsMappingProjectTemplatesRoot, { mappingProjectId }),
                  },
                ],
              },
            ],
          },
          {
            name: 'Master Templates',
            path: RoutePath.translationsMasterTemplatesRoot,
            children: [
              {
                name: 'Create new template',
                path: RoutePath.translationsMasterTemplatesCreate,
              },
              {
                name: 'All templates',
                path: RoutePath.translationsMasterTemplatesList,
              },
            ],
          },
        ],
      },
      {
        name: 'Data Onboarding',
        section: 'products',
        path: RoutePath.onboardingRoot,
        isInternalRoute: true,
        icon: () => <AuditIcon />,
        role: {
          allowList: canAccessOnboarding ? routeAccess.onboardingSidebar : [],
        },
        children: [
          {
            name: 'Configurations',
            path: RoutePath.onboardingConfigurationsRoot,
            children: [
              {
                name: 'All Configurations',
                path: RoutePath.onboardingConfigurationsList,
              },
              {
                name: 'Create New Configuration',
                path: RoutePath.onboardingConfigurationCreateRoot,
                role: { allowList: routeAccess.onboardingConfigurationCreateRoot },
              },
              {
                name: configuration?.name,
                path: generatePath(RoutePath.onboardingConfigurationRoot, { configurationId }),
                children: [
                  {
                    name: 'Client Details',
                    path: generatePath(RoutePath.onboardingConfigurationDetails, { configurationId }),
                  },

                  {
                    name: 'Data dictionary',
                    path: generatePath(RoutePath.onboardingConfigurationDataDictionaryRoot, { configurationId }),
                  },
                  {
                    name: 'Databases',
                    path: generatePath(RoutePath.onboardingConfigurationDatabasesRoot, { configurationId }),
                  },
                  {
                    name: 'Mapping Templates',
                    path: generatePath(RoutePath.onboardingConfigurationTemplatesRoot, { configurationId }),
                  },
                  {
                    name: 'User Permissions',
                    path: generatePath(RoutePath.onboardingConfigurationUsersRoot, { configurationId }),
                  },
                ],
              },
            ],
          },
          {
            name: 'Master Templates',
            path: RoutePath.onboardingMasterTemplatesRoot,
            role: { allowList: routeAccess.onboardingMasterTemplatesRoot },
            children: [
              {
                name: 'All Templates',
                path: RoutePath.onboardingMasterTemplatesList,
              },
              {
                name: 'Create New Template',
                path: RoutePath.onboardingMasterTemplatesCreate,
              },
            ],
          },
          {
            name: 'Master Data Dictionary',
            path: RoutePath.onboardingMasterDataDictionaryRoot,
            role: { allowList: routeAccess.onboardingMasterDataDictionaryRoot },
            children: [
              {
                name: 'All Master Dictionaries',
                path: RoutePath.onboardingMasterDataDictionaryList,
              },
              {
                name: 'Create New Master Dictionary',
                path: RoutePath.onboardingMasterDataDictionaryCreateRoot,
              },
            ],
          },
        ],
      },
    ];
  }, [project, mappingProject, configuration, isProjectDBCreation, canAccessOnboarding]);

  return (
    <aside
      className={classNames([
        styles.Wrapper,
        'is-flex is-flex-direction-column has-background-white is-relative',
        { [styles.IsActive]: isActive },
      ])}
      id="sidebar"
    >
      <header className={classNames(styles.Header, 'is-flex-shrink-0')}>
        <div className="columns is-align-items-center is-gapless mb-0">
          <div className="column is-narrow">
            <LinkWithParams className={classNames(styles.Logo, 'has-icon')} to={RoutePath.root}>
              <img src={brandingLogoUrl} alt={brandingLogoAlt} />
            </LinkWithParams>
          </div>
          {isActive && (
            <div className="column">
              <h2 className="title is-nowrap is-4 ml-4">{brandingName}</h2>
            </div>
          )}
        </div>
        {isActive && <Filters />}
      </header>
      {isLoggedIn && (
        <>
          <button
            className={classNames(styles.ToggleButton, 'has-icon', { 'is-active': isActive })}
            type="button"
            onClick={() => setIsActive(!isActive)}
            aria-controls="sidebar"
            aria-label={isActive ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <span className="icon">
              <HideIcon />
            </span>
          </button>
          {userRole && (
            <Navigation
              isActiveSidebar={isActive}
              clientPortalDomain={clientPortalDomain}
              localConfig={navigationLocalConfig}
              userRoleList={userRoleList}
              isAzureTargetPlatform={isAzureTargetPlatform}
              isCommonItemsCollapse
            />
          )}
        </>
      )}
    </aside>
  );
}

export default Sidebar;
